// Copyright ©️ 2026 eVolve MEP, LLC

import { extractErrorMessage, type UploadableFile } from '@evolvemep/api-ts';
import { notifications } from '@mantine/notifications';

import { isNotNil } from 'helpers/isNotNil';

export const uploadToPresignedUrl = async (
  file: File,
  preSignedURL: string,
  contentType: string | undefined,
  throwOnError = false,
) => {
  try {
    const putRest = await fetch(preSignedURL, {
      method: 'PUT',
      mode: 'cors',
      body: file,
      headers: contentType
        ? {
            'Content-Type': contentType,
          }
        : undefined,
    });
    if (!putRest.ok) throw await putRest.text();
    return true;
  } catch (e) {
    if (throwOnError) throw e;
    const extractedError = extractErrorMessage(e);
    notifications.show({
      title: 'Something went wrong',
      message: `Unable to upload attachment. ${extractedError}`,
      color: 'red',
    });
    return false;
  }
};

export const uploadFileIfNeeded = async (
  file: File | null | undefined,
  preSignedResponse: UploadableFile | null | undefined,
) => {
  if (file && preSignedResponse?.preSignedUrl) {
    return uploadToPresignedUrl(file, preSignedResponse.preSignedUrl, preSignedResponse.contentType);
  }
  return false;
};

export const uploadFilesIfNeeded = async (
  files: File[] | null | undefined,
  preSignedResponses: (UploadableFile | null | undefined)[] | null | undefined,
) =>
  Promise.allSettled(
    files?.map((file) =>
      uploadFileIfNeeded(
        file,
        preSignedResponses?.find((r) => isNotNil(r) && r.fileName === file.name),
      ),
    ) ?? [],
  );
